import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type MutableRefObject, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { CustomerCommand } from '../../../../services/command';
import { updateCustomerRequest } from '../../CustomerManagement.helper';

const EMPTY_OTP = ['', '', '', '', ''];
const DEFAULT_OTP_SECONDS = 180;
const PHONE_ALLOWED_KEYS = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

export const calculateStrokeDashoffset = (timeLeft: number, totalSeconds: number): number => {
    const radius = 35;
    const circumference = 2 * Math.PI * radius;
    const percentage = totalSeconds > 0 ? timeLeft / totalSeconds : 0;
    return circumference * (1 - percentage);
};

export const formatCountdown = (timeLeft: number): string =>
    `${Math.floor(timeLeft / 60)
        .toString()
        .padStart(2, '0')}:${(timeLeft % 60).toString().padStart(2, '0')}`;

const focusOtpSibling = (
    refs: MutableRefObject<(HTMLInputElement | null)[]>,
    index: number,
    value: string
) => {
    if (value.length === 1 && index < refs.current.length - 1) {
        refs.current[index + 1]?.focus();
        return;
    }
    if (value === '' && index > 0) {
        refs.current[index - 1]?.focus();
    }
};

interface UseCustomerCardPaperParams {
    datas: any;
    refresh?: () => void;
}

export const useCustomerCardPaper = ({ datas, refresh }: UseCustomerCardPaperParams) => {
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const removePhoneOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [otpOpen, setOtpOpen] = useState(false);
    const [removePhoneOtpOpen, setRemovePhoneOtpOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('5');
    const [otp, setOtp] = useState([...EMPTY_OTP]);
    const [removePhoneOtp, setRemovePhoneOtp] = useState([...EMPTY_OTP]);
    const [timeLeft, setTimeLeft] = useState(DEFAULT_OTP_SECONDS);
    const [removePhoneTimeLeft, setRemovePhoneTimeLeft] = useState(DEFAULT_OTP_SECONDS);
    const [removePhoneTimeTotal, setRemovePhoneTimeTotal] = useState(DEFAULT_OTP_SECONDS);
    const [timeExpired, setTimeExpired] = useState(false);
    const [removePhoneTimeExpired, setRemovePhoneTimeExpired] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [removePhoneLoading, setRemovePhoneLoading] = useState(false);
    const [removePhoneOtpLoading, setRemovePhoneOtpLoading] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const [customerUpdateRequest, setCustomerUpdatRequest] = useState<updateCustomerRequest>({
        customerId: datas.customer.customerId,
        emailAddress: datas.customer.email,
        firstName: datas.customer.firstName,
        lastName: datas.customer.lastName,
        phoneNumber: phoneNumber,
        hasValidatedPhoneNumber: datas.customer.hasValidatedPhoneNumber,
        dateOfBirth: datas.customer.dateOfBirth,
        emailOptIn: datas.customer.emailOptIn,
        smsOptIn: datas.customer.smsOptIn,
        callOptIn: datas.customer.callOptIn,
        cultureCode: datas.customer.cultureCode,
        languageId: datas.customer.languageId,
    });

    useEffect(() => {
        if (otpOpen && timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
        if (timeLeft === 0) {
            setTimeExpired(true);
        }
    }, [otpOpen, timeLeft]);

    useEffect(() => {
        if (removePhoneOtpOpen && removePhoneTimeLeft > 0) {
            const timer = setTimeout(() => setRemovePhoneTimeLeft(removePhoneTimeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
        if (removePhoneOtpOpen && removePhoneTimeLeft === 0) {
            setRemovePhoneTimeExpired(true);
        }
    }, [removePhoneOtpOpen, removePhoneTimeLeft]);

    useEffect(() => {
        setCustomerUpdatRequest((prev) => ({
            ...prev,
            phoneNumber: phoneNumber,
        }));
    }, [phoneNumber]);

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text ?? '');
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (!value || !value.startsWith('5')) {
            setPhoneNumber('5');
            setPhoneNumberError(true);
            return;
        }

        const numericValue = value.replace(/[^0-9]/g, '');
        const finalValue = numericValue.substring(0, 10);
        setPhoneNumber(finalValue);
        setPhoneNumberError(finalValue.length < 10);
    };

    const handlePhoneNumberKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (PHONE_ALLOWED_KEYS.includes(event.key)) return;

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return;
        }

        const target = event.target as HTMLInputElement;
        if (target.selectionStart === 0 && event.key !== '5') {
            event.preventDefault();
            return;
        }

        if (phoneNumber.length >= 10 && target.selectionStart === target.selectionEnd) {
            event.preventDefault();
        }
    };

    const handlePhoneNumberSelect = (event: SyntheticEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        if (target.selectionStart === 0 && target.selectionEnd === 1) {
            setTimeout(() => {
                target.setSelectionRange(1, 1);
            }, 0);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setPhoneNumber('5');
        setPhoneNumberError(false);
    };

    const handleClose = () => setOpen(false);

    const handleOtpClose = () => {
        setOtpOpen(false);
        setTimeExpired(false);
        setOtp([...EMPTY_OTP]);
    };

    const handleRemovePhoneOtpClose = () => {
        setRemovePhoneOtpOpen(false);
        setRemovePhoneTimeExpired(false);
        setRemovePhoneOtp([...EMPTY_OTP]);
    };

    const handleUpdate = async () => {
        if (phoneNumber.length < 10) {
            setPhoneNumberError(true);
            return;
        }

        const req = { phoneNumber: phoneNumber, customerId: datas.customer.customerId };
        setLoading(true);

        try {
            const isUsed = await CustomerCommand.IsPhoneNumberUsedBeforeThreeTimes(req);
            if (isUsed !== false) {
                setLoading(false);
                Swal.fire(
                    t('customerInformation.phoneNumberUsed'),
                    t('customerInformation.phoneNumberUsedBeforeThreeTimesError'),
                    'error'
                );
                return;
            }

            await CustomerCommand.UpdateCustomer(customerUpdateRequest)
                .then((res: any) => {
                    if (res.error) {
                        Swal.fire('', ' ', 'error');
                    }
                })
                .finally(() => {
                    setLoading(false);
                    setOpen(false);
                    setOtpOpen(true);
                    setTimeLeft(DEFAULT_OTP_SECONDS);
                });
        } catch {
            setLoading(false);
            Swal.fire(t('general.serviceError'), '', 'error');
        }
    };

    const submitOtpVerification = async (otpCode: string) => {
        setLoading(true);
        try {
            const res: any = await CustomerCommand.UpdateCustomerEnd({
                phoneNumber: phoneNumber,
                otpCode: otpCode,
            });

            if (res.error) {
                Swal.fire('', t('general.verificationFailed'), 'error');
                return;
            }

            if (!res.operationStatus) {
                otpRefs.current[0]?.focus();
                Swal.fire('', t('general.otpInvalid'), 'error');
                return;
            }

            Swal.fire('', t('general.phoneNumberVerified'), 'success');
            refresh?.();
            setOtpOpen(false);
        } finally {
            setLoading(false);
            setTimeLeft(DEFAULT_OTP_SECONDS);
            setOtp([...EMPTY_OTP]);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (isNaN(Number(value)) || value.length > 1) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        focusOtpSibling(otpRefs, index, value);

        const otpCode = newOtp.join('');
        if (otpCode.length === 5) {
            submitOtpVerification(otpCode);
        }
    };

    const handleResendCode = () => {
        setLoading(true);
        CustomerCommand.UpdateCustomer(customerUpdateRequest)
            .then((res: any) => {
                if (res.error || res.model.updateCustomerStatus !== 1) {
                    Swal.fire('', t('general.resendCodeFailed'), 'error');
                    return;
                }
                setOtp([...EMPTY_OTP]);
                setTimeLeft(DEFAULT_OTP_SECONDS);
                setTimeExpired(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const openRemovePhoneOtpModal = (expireSeconds: number) => {
        setRemovePhoneTimeLeft(expireSeconds);
        setRemovePhoneTimeTotal(expireSeconds);
        setRemovePhoneTimeExpired(false);
        setRemovePhoneOtp([...EMPTY_OTP]);
        setRemovePhoneOtpOpen(true);
    };

    const handleRemovePhoneNumber = async () => {
        const result = await Swal.fire({
            text: t('customerInformation.removePhoneConfirmMessage'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
        });

        if (!result.isConfirmed) {
            return;
        }

        setRemovePhoneLoading(true);
        try {
            const res = await CustomerCommand.SendConfirmationCode({
                phoneNumber: datas.customer.phoneNumber,
                phoneAreaCode: datas.customer.phoneAreaCode,
            });

            if (res?.status === true) {
                openRemovePhoneOtpModal(res.expireTimeTotalSeconds ?? DEFAULT_OTP_SECONDS);
                return;
            }

            Swal.fire('', res?.message || t('general.serviceError'), 'error');
        } catch (error: any) {
            Swal.fire('', error?.message || t('general.serviceError'), 'error');
        } finally {
            setRemovePhoneLoading(false);
        }
    };

    const handleRemovePhoneOtpChange = (index: number, value: string) => {
        if (isNaN(Number(value)) || value.length > 1) {
            return;
        }

        const newOtp = [...removePhoneOtp];
        newOtp[index] = value;
        setRemovePhoneOtp(newOtp);
        focusOtpSibling(removePhoneOtpRefs, index, value);
    };

    const handleRemovePhoneOtpComplete = async () => {
        const otpCode = removePhoneOtp.join('');
        if (otpCode.length !== 5) {
            return;
        }

        setRemovePhoneOtpLoading(true);
        try {
            const res = await CustomerCommand.ClearCustomerPhoneNumber({
                customerId: datas.customer.customerId,
                phoneNumber: datas.customer.phoneNumber,
                phoneAreaCode: datas.customer.phoneAreaCode,
                otpCode: otpCode,
            });

            if (res?.operationStatus === false) {
                Swal.fire('', res?.errorMessageKeyword || t('general.verificationFailed'), 'error');
                return;
            }

            Swal.fire('', t('customerInformation.removePhoneSuccess'), 'success');
            handleRemovePhoneOtpClose();
            refresh?.();
        } catch (error: any) {
            Swal.fire('', error?.message || t('general.serviceError'), 'error');
        } finally {
            setRemovePhoneOtpLoading(false);
        }
    };

    const handleRemovePhoneResendCode = async () => {
        setRemovePhoneOtpLoading(true);
        try {
            const res = await CustomerCommand.SendConfirmationCode({
                phoneNumber: datas.customer.phoneNumber,
                phoneAreaCode: datas.customer.phoneAreaCode,
            });

            if (res?.status === true) {
                const expireSeconds = res.expireTimeTotalSeconds ?? DEFAULT_OTP_SECONDS;
                setRemovePhoneOtp([...EMPTY_OTP]);
                setRemovePhoneTimeLeft(expireSeconds);
                setRemovePhoneTimeTotal(expireSeconds);
                setRemovePhoneTimeExpired(false);
                return;
            }

            Swal.fire('', res?.message || t('general.resendCodeFailed'), 'error');
        } catch {
            Swal.fire('', t('general.resendCodeFailed'), 'error');
        } finally {
            setRemovePhoneOtpLoading(false);
        }
    };

    const removePhoneDisplayNumber = `${datas.customer.phoneAreaCode ?? ''} ${datas.customer.phoneNumber ?? ''}`.trim();
    const isRemovePhoneOtpCompleteDisabled = removePhoneOtp.join('').length !== 5 || removePhoneOtpLoading;

    return {
        t,
        otpRefs,
        removePhoneOtpRefs,
        open,
        otpOpen,
        removePhoneOtpOpen,
        phoneNumber,
        otp,
        removePhoneOtp,
        timeLeft,
        removePhoneTimeLeft,
        removePhoneTimeTotal,
        timeExpired,
        removePhoneTimeExpired,
        phoneNumberError,
        loading,
        removePhoneLoading,
        removePhoneOtpLoading,
        copiedField,
        removePhoneDisplayNumber,
        isRemovePhoneOtpCompleteDisabled,
        handleCopy,
        handlePhoneNumberChange,
        handlePhoneNumberKeyDown,
        handlePhoneNumberSelect,
        handleOpen,
        handleClose,
        handleOtpClose,
        handleRemovePhoneOtpClose,
        handleUpdate,
        handleOtpChange,
        handleResendCode,
        handleRemovePhoneNumber,
        handleRemovePhoneOtpChange,
        handleRemovePhoneOtpComplete,
        handleRemovePhoneResendCode,
    };
};
